import React, { useEffect, useState } from "react";
import { Shield, ExternalLink, Link as LinkIcon, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { getFallbackPolicy, listFallbackPolicies, getPrivacyPolicyApiBase } from "../lib/privacyPolicies";

const SystemPrivacyPoliciesPage = () => {
  const [policies, setPolicies] = useState(listFallbackPolicies());
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(true);

  useEffect(() => {
    let active = true;
    const apiBase = getPrivacyPolicyApiBase();

    const loadPolicies = async () => {
      try {
        const response = await fetch(`${apiBase}/api/public/privacy-policies`);
        if (!response.ok) {
          throw new Error("Falha ao carregar politicas.");
        }

        const data = await response.json();
        if (active && Array.isArray(data.items) && data.items.length > 0) {
          setPolicies(data.items);
          setUsingFallback(false);
        }
      } catch {
        if (active) {
          setPolicies(listFallbackPolicies());
          setUsingFallback(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPolicies();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Politicas de Privacidade</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Paginas publicas e endpoints oficiais das politicas usadas nos apps publicados.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
          <div className="font-medium text-slate-900">Fonte</div>
          <div>{usingFallback ? "Fallback local" : "API publica"}</div>
        </div>
      </div>

      {loading && (
        <Card>
          <CardContent className="py-8 text-sm text-slate-600">
            Carregando politicas de privacidade...
          </CardContent>
        </Card>
      )}

      {!loading && usingFallback && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 text-sm text-amber-900">
            A listagem foi carregada a partir do fallback local porque a API publica nao respondeu neste acesso.
          </CardContent>
        </Card>
      )}

      {!loading && (
        <div className="grid gap-4">
          {policies.map((policy) => {
            const fallback = getFallbackPolicy(policy.id);
            const publicUrl = policy.page_url || fallback?.page_url;
            const apiUrl = policy.api_url || fallback?.api_url;

            return (
              <Card key={policy.id} className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-900">
                        <Shield className="h-5 w-5 text-cyan-600" />
                        {policy.app_name}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm text-slate-600">
                        {policy.summary}
                      </CardDescription>
                    </div>
                    <div className="text-xs text-slate-500">
                      Atualizada em {policy.last_updated}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                        <FileText className="h-4 w-4 text-cyan-600" />
                        Pagina publica
                      </div>
                      <div className="break-all text-sm text-slate-600">{publicUrl}</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                        <LinkIcon className="h-4 w-4 text-cyan-600" />
                        Endpoint API
                      </div>
                      <div className="break-all text-sm text-slate-600">{apiUrl}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button asChild variant="default">
                      <a href={publicUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Abrir pagina publica
                      </a>
                    </Button>
                    <Button asChild variant="outline">
                      <a href={apiUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Abrir endpoint API
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemPrivacyPoliciesPage;
